// eslint-disable-next-line simple-import-sort/imports
import { useEffect } from 'react'

import '@babel/polyfill'

import { ExtraErrorData } from '@sentry/integrations'
import { init, reactRouterV6Instrumentation, setTag, withProfiler } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { pdfjs } from 'react-pdf'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'

import '@acre/design-system/mui-x-license'
import acreConfig from '@acre/config'
import { muiTheme, muiThemeClarity, ThemeProvider } from '@acre/design-system'
import type { LutherException } from '@acre/graphql'
import { getTeamComponentFromPath, preloadCsClarityFont, useFlag } from '@acre/utils'

import App from './App'
import AppProviders from './AppProviders'

// Set the workerSrc manually because react-pdf no direct support for vite.
//
// File is copied during dev & build via vite-static-copy-plugin [see apps/broker-crm/vite.config.ts].
pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const isPlaywright = navigator.webdriver
// @ts-expect-error - Cypress is a global variable
const isCypress = window.Cypress

// Disable sentry reporting in test environments
if (import.meta.env.MODE === 'production' && !isCypress && !isPlaywright) {
  init({
    dsn: 'https://ee7d1ac2845d4d9a998f53da3575f68a@sentry.io/1876005',
    environment: acreConfig.SENTRY_ENVIRONMENT,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: reactRouterV6Instrumentation(
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        ),
      }),
      new ExtraErrorData({ depth: 5 }),
    ],
    tracesSampler: (samplingContext) => {
      if (samplingContext?.transactionContext?.op === 'navigation') return 0.015 // 1.5%
      // Apply a 0.15% sample rate for all other transactions (10× lower)
      return 0.0015
    },
    beforeSend(event, hint) {
      // This ensures that sentry is grouping our events as we want them, rather than putting all failing API calls together
      const error = hint?.originalException as Error

      // Ignore ApolloErrors
      if (error?.name === 'ApolloError') return null

      const hasGraphqlError =
        event.breadcrumbs?.some(
          (b) => b.category === 'sentry.event' && b.message && /GraphqlError:/i.test(b.message),
        ) || false

      // Skip logging GraphQL errors here, as they're already handled by graphqlErrorLink.
      if (hasGraphqlError) return null

      if (error && typeof error !== 'string' && error.name === 'LutherException') {
        const err = error as LutherException

        event.fingerprint = [err.message]
      }

      // Deducing the team and component from the URL and adding them to the tags list
      const teamTags = getTeamComponentFromPath(window.location.pathname, 'cp')

      event.tags = {
        ...event.tags,
        team: teamTags.team,
        component: teamTags.component,
      }

      return event
    },
  })

  setTag('issue_type', 'frontend')
  setTag('app', 'introducers')
}

// Allows us to use the sentry profiler, can track render times for the app component and descendants
const AppWithProfiler = withProfiler(App)

preloadCsClarityFont()

const ThemedApp = () => {
  const scoreFont = useFlag('feature.score_font', false)
  return (
    <ThemeProvider theme={scoreFont ? muiThemeClarity : muiTheme}>
      <AppWithProfiler />
    </ThemeProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <AppProviders>
    <ThemedApp />
  </AppProviders>,
)
